import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';

// Components
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ShowcaseSection from './components/ShowcaseSection';
import ServicesSection from './components/ServicesSection';
import InteractiveDemoSection from './components/InteractiveDemoSection';
import AppDownloadSection from './components/AppDownloadSection';
import PricingPlansSection from './components/PricingPlansSection';
import ProjectsSection from './components/ProjectsSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import AuthModal from './components/AuthModal';
import WhatsAppWidget from './components/WhatsAppWidget';
import { useAuthStore } from './store/authStore';

// E1 — real public legal pages (replace the old modal-based placeholders)
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import Terms from './pages/legal/Terms';
import Support from './pages/legal/Support';
import AccountDeletion from './pages/legal/AccountDeletion';

// E1 — website customer-dashboard foundation
import DashboardLayout from './pages/dashboard/DashboardLayout';
import DashboardHome from './pages/dashboard/DashboardHome';
import MyHomes from './pages/dashboard/MyHomes';
import MyDevices from './pages/dashboard/MyDevices';
import AddDevice from './pages/dashboard/AddDevice';
import Gateways from './pages/dashboard/Gateways';
import Cameras from './pages/dashboard/Cameras';
import Automations from './pages/dashboard/Automations';
import Scenes from './pages/dashboard/Scenes';
import Energy from './pages/dashboard/Energy';
import Shop from './pages/dashboard/Shop';
import Orders from './pages/dashboard/Orders';
import CompatibleDevices from './pages/dashboard/CompatibleDevices';
import DashboardSupport from './pages/dashboard/DashboardSupport';
import Account from './pages/dashboard/Account';

function HomePage({ onLoginClick, onRegisterClick, handleGetStarted }) {
  return (
    <>
      <Navbar onLoginClick={onLoginClick} onRegisterClick={onRegisterClick} />
      <main>
        <HeroSection onGetStarted={handleGetStarted} />
        <AboutSection />
        <ShowcaseSection />
        <ServicesSection />
        <InteractiveDemoSection />
        <AppDownloadSection />
        <PricingPlansSection />
        <ProjectsSection />
        <ContactSection />
      </main>
      <Footer />
      <WhatsAppWidget />
    </>
  );
}

function App() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'

  const handleLoginClick = () => {
    setAuthMode('login');
    setShowAuthModal(true);
  };

  const handleRegisterClick = () => {
    setAuthMode('register');
    setShowAuthModal(true);
  };

  const handleSwitchMode = () => {
    setAuthMode(authMode === 'login' ? 'register' : 'login');
  };

  const handleGetStarted = () => {
    // E1: was `localStorage.getItem('token')` — now checks the real
    // cookie-backed session state via the auth store, exactly like every
    // other auth-aware component in this app.
    const user = useAuthStore.getState().user;
    if (user) {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      handleRegisterClick();
    }
  };

  return (
    <Router>
      <div className="App min-h-screen bg-[#0a0e27]">
        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                onLoginClick={handleLoginClick}
                onRegisterClick={handleRegisterClick}
                handleGetStarted={handleGetStarted}
              />
            }
          />

          {/* E1 — real, public, standalone legal pages */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          {/* E1 Final Review finding: frontend/app.json's privacyPolicyUrl
              points at the short form (/privacy) from an earlier mobile
              config phase — redirect rather than silently 404 or rename
              the canonical route out from under it. */}
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/support" element={<Support />} />
          <Route path="/account-deletion" element={<AccountDeletion />} />

          {/* E1 — website customer-dashboard foundation (requires a real,
              canonical Sheba session — DashboardLayout redirects home if
              not authenticated) */}
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<DashboardHome />} />
            <Route path="homes" element={<MyHomes />} />
            <Route path="devices" element={<MyDevices />} />
            <Route path="devices/add" element={<AddDevice />} />
            <Route path="gateways" element={<Gateways />} />
            <Route path="cameras" element={<Cameras />} />
            <Route path="automations" element={<Automations />} />
            <Route path="scenes" element={<Scenes />} />
            <Route path="energy" element={<Energy />} />
            <Route path="shop" element={<Shop />} />
            <Route path="orders" element={<Orders />} />
            <Route path="compatible-devices" element={<CompatibleDevices />} />
            <Route path="support" element={<DashboardSupport />} />
            <Route path="account" element={<Account />} />
          </Route>

          {/* Any unmatched path (e.g. a stale/bad link) — redirect home
              rather than rendering a blank page. Matches the pattern
              already used in admin/customer's own routers. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* Auth Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          mode={authMode}
          onSwitchMode={handleSwitchMode}
        />
      </div>
    </Router>
  );
}

export default App;
